/**
 * WalletAdapterContext publishes the active adapter's live controller down the
 * tree so `useWallet` (deep inside Web3ReactProvider) and `WalletBridge` can reach
 * it without knowing which SDK is mounted.
 *
 * `WalletAdapterPublisher` runs `adapter.useController()` — which is why it must be
 * rendered *inside* the SDK's Provider — and pushes the result into context. For
 * the default web3-react kind the controller is the inert passthrough, so nothing
 * downstream changes.
 */
import React, { createContext, useContext } from "react";
import { WalletProviderKind } from "../../../enums/walletProvider";
import type { WalletAdapter, WalletAdapterController } from "./types";

const WEB3REACT_FALLBACK: WalletAdapterController = {
  kind: WalletProviderKind.WEB3REACT,
  isReady: true,
  connect: () =>
    Promise.reject(new Error("No wallet adapter controller mounted")),
  disconnect: () => Promise.resolve(),
  getProvider: () => undefined,
  getAccount: () => undefined,
  hasSession: () => false,
};

const WalletAdapterContext =
  createContext<WalletAdapterController>(WEB3REACT_FALLBACK);

/** Access the active wallet adapter controller. */
export const useWalletAdapter = (): WalletAdapterController =>
  useContext(WalletAdapterContext);

/**
 * Runs the adapter's controller hook (inside the SDK Provider) and provides it to
 * the subtree. Rendered by `WalletProviderGate` between the SDK Provider and the
 * rest of the app.
 */
export function WalletAdapterPublisher({
  adapter,
  children,
}: {
  adapter: WalletAdapter;
  children: React.ReactNode;
}) {
  const controller = adapter.useController();
  return (
    <WalletAdapterContext.Provider value={controller}>
      {children}
    </WalletAdapterContext.Provider>
  );
}
